import { init as initFullStory } from '@fullstory/browser';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import 'primeicons/primeicons.css';
import { PrimeReactProvider } from 'primereact/api';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { UAParser } from 'ua-parser-js';
import './globals.css';
import './logging-config';

import App from './App';
import {
    ErrorBoundary,
    RootErrorBoundaryFallback,
} from './components/shared/components/ErrorBoundary';
import { Config } from './components/shared/utils/config';
import { getOktaAuthEmail } from './components/shared/utils/sessionStorageUtils';

// Initialize FullStory before React renders to capture the full session from first paint
if (Config.FullStoryOrganizationId) {
    try {
        initFullStory({ orgId: Config.FullStoryOrganizationId });
    } catch (err: unknown) {
        console.error('FullStory init error: ', err);
    }
} else if (Config.Environment !== 'local') {
    console.warn('FullStory organization ID is not configured. Session recording is disabled.');
}

const getQueryParam = (param: string) => {
    return new URLSearchParams(window.location.search).get(param);
};

const {
    browser: { name: browserName },
} = UAParser(window.navigator.userAgent);

const LDProvider = withLDProvider({
    clientSideID: Config.LaunchDarklyKey,
    deferInitialization: true,
    reactOptions: { useCamelCaseFlagKeys: false },
    options: {
        logger: {
            error: () => {},
            warn: () => {},
            debug: () => {},
            info: () => {},
        },
    },
    context: {
        kind: 'client',
        anonymous: true,
        key: getOktaAuthEmail() || 'anonymous',
        LDTestId: getQueryParam('LDTestId'),
        browserUrl: window.location.hostname,
        deviceType: browserName,
    },
})(App);

const element = document.getElementById('root')!;

createRoot(element).render(
    <StrictMode>
        <ErrorBoundary location="root" fallback={<RootErrorBoundaryFallback />}>
            <BrowserRouter>
                <PrimeReactProvider>
                    <LDProvider />
                </PrimeReactProvider>
            </BrowserRouter>
        </ErrorBoundary>
    </StrictMode>,
);
